import _                              from 'underscore';
import moment, { Moment, unitOfTime } from 'moment';
import React, { FC, useContext }      from 'react';
import { useFormikContext }           from 'formik';
import { Cell, Grid }                 from "styled-css-grid";
import { Label, List, Segment }       from 'semantic-ui-react';

import { useAuditRecords, useCustomFormikHelpers, useFeatureFlags, useProjectActivities, useProjectTermDictionary }  from '../../../../hooks';
import { combineValuesByDelimiter, formatOption, generateStepwiseOptions, roundToPrecision, splitValuesByDelimiter } from '../../../../util';
import { ProjectService, TimeService }                                                                               from '../../../../services';
import { TIME_UNITS }                                                                                                from '../../../../constants/TimeEnums';
import { CorvalProject, CorvalRecurrenceType, SystemEvent }                                                          from '../../../../types';
import { PROJECT_MILESTONES, ProjectMilestoneTypes }                                                                 from '../../../../constants/ProductEnums';
import { ProjectQueries }                                                                                            from '../../../../queries/ProjectQueries';
import { STRING_CONVERSION }                                                                                         from '../../../../constants/FormatStringEnums';
import { AUDIT_RECORD_TYPE }                                                                                         from '../../../../constants/SystemRecordEnums';
import { DateField, FieldDescription, FieldWrapper, SelectField }                                                    from '../../../components/forms';
import { FieldLabel }                                                                                                from '../../../components/forms/formik/FieldLabel';
import { FinePrintMessage }                                                                                          from '../../../components/util/messaging/FinePrintMessage';
import { ProjectTimeMilestoneOffsetDropdown, ProjectTimeMilestonesDropdown }                                         from '../../../components/forms/dropdowns';
import { ProjectDetails }                                                                                            from '../../../components/from-query/ProjectDetails';
import { CorvalRecommendedStatement }                                                                                from '../../../components/product/CorvalRecommendedStatement';
import { InfoMessage }                                                                                               from '../../../components/util/messaging/InfoMessage';
import { HelpAndSupportStatement }                                                                                   from '../../../components/util/messaging/HelpAndSupportStatement';
import { StringOrEmpty }                                                                                             from '../../../components/display/StringOrEmpty';
import { TextLink }                                                                                                  from '../../../components/display/TextLink';
import { LastUpdatedAuditTag }                                                                                       from '../../../components/audits/LastUpdatedAuditTag';
import { DangerMessage }                                                                                             from '../../../components/util/messaging/DangerMessage';
import { ToggleViewControl }                                                                                         from '../common/visualization/ui-manipulation/ToggleViewControl';
import { ProjectActivityUpdateDialogContext, ProjectActivityUpdateDialogFormValues }                                 from './ProjectActivityUpdateDialog';
import { Tooltip }                                                                                                   from "../../../components/util/messaging/ToolTip";


const { DELIMITER } = STRING_CONVERSION;

export enum PROJECT_ACTIVITY_UPDATE_TIMING_MODE {
    START_END_DATE,
    MILESTONE_DURATION,
}

export interface ProjectActivityUpdateAreaTimingDisplayOptions {
    showStartDate?: boolean,
    showEndDate?: boolean,
    showDuration?: boolean,
    showMilestoneTiming?: boolean,
    showRecurrenceScheduling?: boolean,
}

interface Props extends ProjectActivityUpdateAreaTimingDisplayOptions {}

const defaultProps: Props = {
    showStartDate:            false,
    showEndDate:              false,
    showDuration:             false,
    showMilestoneTiming:      true,
    showRecurrenceScheduling: true,
}

const ProjectActivityUpdateAreaTiming: FC<Props> = ( props ) => {

    const { showStartDate, showEndDate, showDuration }                     = props,
          { showMilestoneTiming, showRecurrenceScheduling }                = props,
          { projectId, projectActivity, timelineFields, isLoading }        = useContext( ProjectActivityUpdateDialogContext ),
          { projectTimingOverrideType, setProjectTimingOverrideType }      = useContext( ProjectActivityUpdateDialogContext ),
          { project: { mapLowerBound, mapUpperBound } }                    = projectActivity,
          { termDictionary }                                               = useProjectTermDictionary(),
          { getProjectActivityDuration, getProjectActivityDurationInDays } = useProjectActivities(),
          { getAuditRecordsFromEntity, getAuditRecordTypes }               = useAuditRecords(),
          { includesErrorsIn }                                             = useCustomFormikHelpers(),
          form                                                             = useFormikContext<ProjectActivityUpdateDialogFormValues>(),
          featureFlags                                                     = useFeatureFlags();

    // Recurrence state record keeping
    const canEditRecurrence             = featureFlags.enableProjectActivityRecurrenceEditing,
          hasRecurrenceSchedule         = (
              !( _.isNull( form.values.recurrenceValueOverride ) ) &&
              !( _.isNull( form.values.recurrenceUnitOverride ) || form.values.recurrenceUnitOverride == '' )
          ),
          hadPreviousRecurrenceSchedule = (
              !( _.isNull( form.initialValues.recurrenceValueOverride ) ) &&
              !( _.isNull( form.initialValues.recurrenceUnitOverride ) || form.initialValues.recurrenceUnitOverride == '' )
          );

    // Recurrence start / end date record keeping
    const hypotheticalStartTime = TimeService.ISOtoMoment( projectActivity.startTimeUtc ),
          hypotheticalIncrement = form.values.recurrenceValueOverride,
          hypotheticalUnit      = form.values.recurrenceUnitOverride as unitOfTime.Diff,
          hypotheticalEndTime   = TimeService.ISOtoMoment( projectActivity.startTimeUtc ).add( 20, 'years' );

    // Recurrence date samples
    const sampleDates = ( !hadPreviousRecurrenceSchedule && hasRecurrenceSchedule )
                        ? TimeService.getReoccuringDateSet( hypotheticalStartTime, hypotheticalIncrement, hypotheticalUnit, hypotheticalEndTime, )
                        : null;

    // Milestone type record keeping
    const isMilestoneTimingType     = ( projectTimingOverrideType === PROJECT_ACTIVITY_UPDATE_TIMING_MODE.MILESTONE_DURATION ),
          isMilestoneRecurrenceType = ( form.values.recurrenceTypeOverride === CorvalRecurrenceType.Milestone );

    // Duration type options
    const timingDurationOptions = [
        formatOption( TIME_UNITS.DAYS, 'Day(s)' ),
        formatOption( TIME_UNITS.WEEKS, 'Week(s)' ),
        formatOption( TIME_UNITS.MONTHS, 'Month(s)' ),
    ];

    // Duration calue options
    const timingDurationValueOptions = [
        ...generateStepwiseOptions( 1, 13, 1, '' ).map( option => ( { ...option, group: 'Common' } ) ),
        ...generateStepwiseOptions( 24, 73, 12, '' ).map( option => ( { ...option, group: 'Periodic' } ) ),
        ...generateStepwiseOptions( 13, 365, 1, '' ).map( option => ( { ...option, group: 'Linear' } ) ),
    ];

    // Recurrence frequency options
    const recurrenceFrequencyOptions = [
        formatOption( combineValuesByDelimiter( [ '3', TIME_UNITS.MONTHS ] ), '3 Months' ),
        formatOption( combineValuesByDelimiter( [ '6', TIME_UNITS.MONTHS ] ), '6 Months' ),
        formatOption( combineValuesByDelimiter( [ '9', TIME_UNITS.MONTHS ] ), '9 Months' ),
        formatOption( combineValuesByDelimiter( [ '12', TIME_UNITS.MONTHS ] ), '12 Months' ),
    ];

    const handleRecurrenceFrequencyChanged = ( incomingValue: string ) => {

        let [ value, unit ] = incomingValue ? splitValuesByDelimiter( incomingValue ) : [ null, null ];
        let numericValue = parseInt( value ) ?? null

        form.setValues( {
            ...form.values,
            recurrenceValueOverride:     numericValue,
            recurrenceUnitOverride:      unit ?? null,
            recurrenceValueUnitOverride: ( unit && value ) ? combineValuesByDelimiter( [ value, unit ] ) : null
        } )
    }

    const handleTimingTypeChanged = ( event: SystemEvent, data: { checked: boolean } ) => {

        const { durationValue, durationUnit } = getProjectActivityDuration( projectActivity, true ) as { durationValue: number, durationUnit: TIME_UNITS };

        const prefillDefaults = {
            projectTimingMilestoneOffsetMonthsOverride: _.isNull( form.initialValues.projectTimingMilestoneOffsetMonthsOverride ) ? 0 : form.initialValues.projectTimingMilestoneOffsetMonthsOverride,
            defaultTimeSpanValueOverride:               _.isNull( form.initialValues.defaultTimeSpanValueOverride ) ? durationValue : form.initialValues.defaultTimeSpanValueOverride,
            defaultTimeSpanUnitOverride:                _.isNull( form.initialValues.defaultTimeSpanUnitOverride ) ? durationUnit : form.initialValues.defaultTimeSpanUnitOverride,
        }

        // clear the other timing type properties out, or revert to the original values
        form.setValues( {
            ...form.values,
            startTimeOverrideUtc:           data.checked ? null : form.initialValues.startTimeOverrideUtc,
            endTimeOverrideUtc:             data.checked ? null : form.initialValues.endTimeOverrideUtc,
            projectTimingMilestoneOverride: data.checked ? form.initialValues.projectTimingMilestoneOverride : null,

            projectTimingMilestoneOffsetMonthsOverride: data.checked ? prefillDefaults.projectTimingMilestoneOffsetMonthsOverride : null,
            defaultTimeSpanValueOverride:               data.checked ? prefillDefaults.defaultTimeSpanValueOverride : null,
            defaultTimeSpanUnitOverride:                data.checked ? prefillDefaults.defaultTimeSpanUnitOverride : null,
        }, false );

        setProjectTimingOverrideType(
            data.checked
            ? PROJECT_ACTIVITY_UPDATE_TIMING_MODE.MILESTONE_DURATION
            : PROJECT_ACTIVITY_UPDATE_TIMING_MODE.START_END_DATE
        );
    }

    const handleRecurrenceTypeChanged = ( event: SystemEvent, data: { checked: boolean } ) => {

        form.setValues( {
            ...form.values,
            recurrenceTypeOverride:       data.checked
                                          ? CorvalRecurrenceType.Milestone
                                          : CorvalRecurrenceType.Periodic,
            recurrenceValueOverride:      null,
            recurrenceUnitOverride:       null,
            recurrenceValueUnitOverride:  null,
            recurrenceMilestonesOverride: []
        } )
    }

    const handleAddRecommendedDurationToCustomStartTime = ( duration: number = 0 ) => {

        if( form.values.endTimeOverrideUtc && duration > 0 ){
            form.setFieldValue(
                'startTimeOverrideUtc',
                TimeService.asDisplayDate(
                    TimeService.displayDateToMoment( form.values.endTimeOverrideUtc ).subtract( duration, 'days' )
                )
            )
        }
    }

    const handleAddRecommendedDurationToCustomEndTime = ( duration: number = 0 ) => {

        if( form.values.startTimeOverrideUtc && duration > 0 ){
            form.setFieldValue(
                'endTimeOverrideUtc',
                TimeService.asDisplayDate(
                    TimeService.displayDateToMoment( form.values.startTimeOverrideUtc ).add( duration, 'days' )
                )
            )
        }
    }

    const handleRevertStartTime = () => {

        form.setValues( {
            ...form.values,
            startTimeOverrideUtc: TimeService.ISOtoDisplayDate( projectActivity.startTimeCalculatedUtc ),
            endTimeOverrideUtc:   null,
        } );
    }

    const handleRevertEndTime = () => {

        form.setValues( {
            ...form.values,
            startTimeOverrideUtc: null,
            endTimeOverrideUtc:   TimeService.ISOtoDisplayDate( projectActivity.endTimeCalculatedUtc ),
        } );
    }

    // Render variables / data
    const customStartDate              = form.values.startTimeOverrideUtc,
          customEndDate                = form.values.endTimeOverrideUtc,
          customMilestoneTiming        = form.values.projectTimingMilestoneOverride,
          customMilestoneOffset        = form.values.projectTimingMilestoneOffsetMonthsOverride,
          customMilestoneDurationValue = form.values.defaultTimeSpanValueOverride,
          customMilestoneDurationUnit  = form.values.defaultTimeSpanUnitOverride,
          boundaryStartDate            = TimeService.ISOtoMoment( projectActivity.earliestAvailableStartDateUtc ),
          hasCustomStartDate           = ( !_.isEmpty( customStartDate ) ),
          hasCustomEndDate             = ( !_.isEmpty( customEndDate ) ),
          hasCustomMilestoneTiming     = ( !_.isEmpty( customMilestoneTiming ) ),
          hasCustomMilestoneOffset     = ( !_.isNull( customMilestoneOffset ) && !_.isUndefined( customMilestoneOffset ) ),
          hasCustomMilestoneDuration   = ( !_.isNull( customMilestoneDurationValue ) && !_.isUndefined( customMilestoneDurationValue ) && !_.isNull( customMilestoneDurationUnit ) && !_.isUndefined( customMilestoneDurationUnit ) ),
          customStartDateMoment        = ( hasCustomStartDate ) ? TimeService.displayDateToMoment( customStartDate ) : null,
          customEndDateMoment          = ( hasCustomEndDate ) ? TimeService.displayDateToMoment( customEndDate ) : null,
          mapLowerBoundMoment          = TimeService.ISOtoMoment( mapLowerBound ),
          mapUpperBoundMoment          = TimeService.ISOtoMoment( mapUpperBound );

    const auditEntries          = getAuditRecordsFromEntity( projectActivity ) ?? [],
          timingAuditEntries    = getAuditRecordTypes( auditEntries, [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_START_END_OVERRIDE_CHANGED ], 'newest' ),
          hasTimingAuditEntries = !_.isEmpty( timingAuditEntries );

    /* Decode DEM recommended duration */
    const projectActivityDurationString = getProjectActivityDuration( projectActivity ),
          projectActivityDurationInDays = getProjectActivityDurationInDays( projectActivity );

    const maxStartDateAvailable                     = ( hasCustomEndDate ) ? TimeService.asDisplayDate( customEndDateMoment.clone().subtract( 1, 'day' ) ) : null,
          minEndDateAvailable                       = ( hasCustomStartDate ) ? TimeService.asDisplayDate( customStartDateMoment.clone().add( 1, 'day' ) ) : null,
          originalStartTimeDate                     = TimeService.ISOtoDisplayDate( projectActivity.startTimeCalculatedUtc ),
          originalEndTimeDate                       = TimeService.ISOtoDisplayDate( projectActivity.endTimeCalculatedUtc ),
          canEditStartTime                          = featureFlags.permissionProjectActivityUpdateStartDate,
          canEditEndTime                            = featureFlags.permissionProjectActivityUpdateEndDate,
          canEditTimingMilestone                    = featureFlags.permissionProjectActivityUpdateTimingMilestone,
          canEditTimingDuration                     = featureFlags.permissionProjectActivityUpdateDuration,
          customStartTimeUnavailableForEndTimeEdits = ( canEditEndTime && !canEditStartTime && !hasCustomStartDate ),
          customEndTimeUnavailableForStartTimeEdits = ( canEditStartTime && !canEditEndTime && !hasCustomEndDate ),
          customMilestoneTimingUnavailableForEdits  = ( !canEditTimingMilestone && !canEditTimingDuration ),
          showOriginalDates                         = ( !hasCustomStartDate && !hasCustomEndDate ) || ( hasCustomStartDate && hasCustomEndDate ),
          showStartDatePrefill                      = ( canEditStartTime && !hasCustomStartDate && hasCustomEndDate && projectActivityDurationInDays ),
          showEndDatePrefill                        = ( canEditEndTime && hasCustomStartDate && !hasCustomEndDate && projectActivityDurationInDays ),
          hasParentSchedulingConflict               = includesErrorsIn( form, 'startTimeOverrideUtc' ) && ( form.errors.startTimeOverrideUtc === 'Start date must come after its parent dependency.' );

    let dateRangeInfo: {
            startDate: Moment,
            endDate: Moment,
            string: string,
            parts: Partial<Record<TIME_UNITS, { value: number, name: string }>>,
            totalDays: number
        },
        dateRangePercentageDiff: number, // actual range difference in days
        dateRangeDiffVariance: number,  // range boundary width; eg: 0 being precise difference, 2 being a range of +/- 2 for difference when comparing to the recommended
        dateRangePercentageDiffDescriptor: string,
        customDurationInDays: number;

    /* Determine useful metrics for custom timing based on start / end date */
    if( hasCustomStartDate && hasCustomEndDate ){

        dateRangeInfo = {
            ...TimeService.getHumanizedDateRangeDiffInfo( customEndDateMoment, customStartDateMoment, ),
            startDate: customEndDateMoment,
            endDate:   customStartDateMoment,
        };

        try{
            customDurationInDays = dateRangeInfo.totalDays;
            dateRangePercentageDiff = roundToPrecision( ( ( customDurationInDays - projectActivityDurationInDays ) / projectActivityDurationInDays ) * 100, 0 );
            dateRangePercentageDiffDescriptor = ( dateRangePercentageDiff > 1 ) ? 'increase' : ( dateRangePercentageDiff < 0 ) ? 'decrease' : '';
            dateRangeDiffVariance = 0; // we want to be specific with the difference

        } catch( e ) {

            // bad values?
        }
    }

    /* Determine useful metrics for custom timing based on milestone / offset / duration */
    if( hasCustomMilestoneTiming && hasCustomMilestoneOffset && hasCustomMilestoneDuration ){

        const timeStart = TimeService.ISOtoMoment( ProjectService.getTimelineDataObjectFromSet( customMilestoneTiming, timelineFields ).rawValue ).add( customMilestoneOffset, 'months' ),
              timeEnd   = timeStart.clone().add( customMilestoneDurationValue, customMilestoneDurationUnit as any );

        dateRangeInfo = {
            ...TimeService.getHumanizedDateRangeDiffInfo( timeEnd, timeStart, true ),
            startDate: timeStart,
            endDate:   timeEnd,
        };

        try{
            customDurationInDays = dateRangeInfo.totalDays;
            dateRangePercentageDiff = roundToPrecision( ( ( customDurationInDays - projectActivityDurationInDays ) / projectActivityDurationInDays ) * 100, 0 );
            dateRangePercentageDiffDescriptor = ( dateRangePercentageDiff > 1 ) ? 'increase' : ( dateRangePercentageDiff < 0 ) ? 'decrease' : '';
            dateRangeDiffVariance = 2; // we want to be generalized with the difference, since we are using imprecise measurements

        } catch( e ) {

            // bad values?
        }
    }

    // Timing layout grid props
    const timingGridProps = {
        startEndDate:      {
            columns:   `.5fr .5fr 1fr`,
            rows:      `auto 25px repeat(3, auto)`,
            columnGap: `18px`,
            areas:     [
                'warning warning warning',
                'start end . ',
                'start end rec',
                'shortcutStartDate shortcutEndDate .',
                'summary summary summary'
            ]
        },
        milestoneDuration: {
            columns:   `2fr 1fr .75fr .75fr`,
            rows:      `auto auto auto auto`,
            columnGap: `6px`,
            areas:     [
                'warning warning warning warning',
                'milestone offset duration durationUnit',
                'earliestDate rec rec rec',
                'summary summary summary summary'
            ]
        }
    }

    return (

        <>
            <Grid columns={ 'auto' } rows={ 'auto' } rowGap={ '8px' }>

                { ( showStartDate || showEndDate || showMilestoneTiming ) && (
                    <Cell>
                        <FieldWrapper>
                            <FieldLabel>
                                <Grid columns={ `1fr auto` } rows={ 'auto' } className={ 'margin-sm-bottom' }>
                                    <Cell>Activity Timing</Cell>
                                    <Cell>{ hasTimingAuditEntries && <LastUpdatedAuditTag useAvatar={ true } useIcon={ false } records={ timingAuditEntries }/> }</Cell>
                                </Grid>
                            </FieldLabel>

                            <Grid columns={ `1fr 180px` }>
                                <Cell>

                                    <FieldDescription>
                                        <FinePrintMessage as={ 'span' }>
                                            {
                                                ( isMilestoneTimingType )
                                                ? <>Customize this activity to follow a specific milestone with an optional month offset, updating it's duration and placing it where it makes sense for your project. When moving an activity, only child dependencies will follow its timing; parent
                                                    dependencies will become detached when scheduling is calculated.</>
                                                : <>Customize when this activity starts and ends, updating it's duration and placing it where it makes sense for your project. When moving an activity, only child dependencies will follow its timing; parent dependencies will become detached when
                                                    scheduling is calculated.</>
                                            }
                                        </FinePrintMessage>
                                    </FieldDescription>

                                </Cell>
                                <Cell style={ { display: 'grid', justifyContent: 'flex-end', alignItems: 'flex-end' } }>

                                    <Grid columns={ `auto 1fr` }
                                          style={ {
                                              transform:       'scale(0.8)',
                                              transformOrigin: 'top center',
                                          } }>

                                        <Cell middle>

                                            <ToggleViewControl
                                                key={ `milestoneTypeOverride-${ isMilestoneTimingType }` }
                                                toggle={ true }
                                                defaultChecked={ isMilestoneTimingType }
                                                onChange={ handleTimingTypeChanged }
                                            />

                                        </Cell>
                                        <Cell middle onClick={ e => handleTimingTypeChanged( e, { checked: !isMilestoneTimingType } ) } className={ 'cursor--pointer' }>

                                            <span>Use Milestones</span>

                                        </Cell>
                                    </Grid>

                                </Cell>
                            </Grid>
                        </FieldWrapper>

                        <Grid { ...( isMilestoneTimingType
                                     ? timingGridProps.milestoneDuration
                                     : timingGridProps.startEndDate ) }>

                            <Cell area={ 'warning' }>

                                {/* No permissions for editing end date w/o start date */ }
                                { hasParentSchedulingConflict &&
                                  <DangerMessage size={ 'mini' } icon={ null }>Due to parent dependency scheduling, the earliest date you can set is <strong>{ TimeService.ISOtoDisplayDate( projectActivity.earliestAvailableStartDateUtc ) }</strong>.</DangerMessage>
                                }

                            </Cell>

                            { !isMilestoneTimingType && (
                                <>
                                    {/* Start Date Controls */ }
                                    <Cell area={ 'start' }>
                                        <DateField
                                            name={ 'startTimeOverrideUtc' }
                                            label={ 'Custom Start Date' }
                                            minDate={ boundaryStartDate }
                                            maxDate={ maxStartDateAvailable }
                                            disabled={ isLoading || !canEditStartTime }
                                            includeErrorMessage={ includesErrorsIn( form, 'startTimeOverrideUtc' ) }
                                        />
                                    </Cell>

                                    {/* Start Date Shortcuts */ }
                                    <Cell middle area={ 'shortcutStartDate' }>

                                        { showOriginalDates &&
                                          <p className={ 'text--xs text--fine-print' }>
                                              Orginally:
                                              { ' ' }
                                              { canEditStartTime ? <TextLink onClick={ () => handleRevertStartTime() }>{ originalStartTimeDate }</TextLink> : originalStartTimeDate }
                                          </p>
                                        }

                                        { showStartDatePrefill &&
                                          <TextLink className={ 'text--sm' } onClick={ () => handleAddRecommendedDurationToCustomStartTime( projectActivityDurationInDays ) }>Set { projectActivityDurationString } Before</TextLink>
                                        }

                                    </Cell>

                                    {/* End Date Control */ }
                                    <Cell area={ 'end' }>
                                        <DateField
                                            name={ 'endTimeOverrideUtc' }
                                            label={ 'Custom End Date' }
                                            minDate={ minEndDateAvailable }
                                            disabled={ isLoading || !canEditEndTime || ( canEditEndTime && !canEditStartTime && !hasCustomStartDate ) }
                                            includeErrorMessage={ includesErrorsIn( form, 'endTimeOverrideUtc' ) }
                                        />
                                    </Cell>

                                    {/* End Date Shortcuts*/ }
                                    <Cell middle area={ 'shortcutEndDate' }>

                                        { showOriginalDates &&
                                          <p className={ 'text--xs text--fine-print' }>
                                              Orginally:
                                              { ' ' }
                                              { canEditEndTime ? <TextLink onClick={ () => handleRevertEndTime() }>{ originalEndTimeDate }</TextLink> : originalEndTimeDate }
                                          </p>
                                        }

                                        { showEndDatePrefill &&
                                          <TextLink className={ 'text--sm' } onClick={ () => handleAddRecommendedDurationToCustomEndTime( projectActivityDurationInDays ) }>Add { projectActivityDurationString }</TextLink>
                                        }

                                    </Cell>
                                </>
                            ) }

                            { isMilestoneTimingType && (
                                <>
                                    {/* Milestone Selection */ }
                                    <Cell area={ 'milestone' } style={ { justifyContent: 'flex-start' } }>

                                        <SelectField
                                            label={ 'Milestone' }
                                            name={ 'projectTimingMilestoneOverride' }
                                            fluid={ true }
                                            clearable={ true }
                                            onChange={ value => {
                                                if( _.isEmpty( value ) ){
                                                    form.setValues( {
                                                        ...form.values,
                                                        projectTimingMilestoneOverride:             null,
                                                        projectTimingMilestoneOffsetMonthsOverride: null,
                                                        defaultTimeSpanValueOverride:               null,
                                                        defaultTimeSpanUnitOverride:                null,
                                                    }, true )
                                                }
                                            } }
                                            disabled={ isLoading || !canEditTimingMilestone }
                                            options={ timelineFields.map( m => formatOption( m.fieldId, `${ m.label } (${ m.value })` ) ) }
                                            omit={ [ "customStartDateUtc", ...timelineFields.filter( field => {

                                                if( !boundaryStartDate ) return false;

                                                return ( TimeService.ISOtoMoment( field.rawValue ).isBefore( boundaryStartDate ) );

                                            } ).map( m => m.fieldId ) ] }
                                        />

                                    </Cell>

                                    {/* Month Offset */ }
                                    <Cell middle area={ 'offset' } style={ { justifyContent: 'flex-start' } }>

                                        <ProjectTimeMilestoneOffsetDropdown
                                            maxOffset={ 24 }
                                            label={ 'Offset' }
                                            name={ 'projectTimingMilestoneOffsetMonthsOverride' }
                                            fluid={ true }
                                            search={ true }
                                            clearable={ true }
                                            disabled={ isLoading || !canEditTimingMilestone }
                                            relativeMilestone={ customMilestoneTiming }
                                            offsetBoundaryDates={ [ moment.max( [ boundaryStartDate, mapLowerBoundMoment ] ), mapUpperBoundMoment ] }
                                            relativeMilestoneDate={ customMilestoneTiming ? ProjectService.getTimelineDataObjectFromSet( customMilestoneTiming, timelineFields ).rawValue : null }
                                        />

                                    </Cell>

                                    {/* Duration */ }
                                    <Cell area={ 'duration' } style={ { justifyContent: 'flex-start' } }>
                                        <SelectField
                                            label={ 'Duration' }
                                            name={ 'defaultTimeSpanValueOverride' }
                                            fluid={ true }
                                            search={ true }
                                            useGroupNames={ true }
                                            clearable={ true }
                                            disabled={ isLoading || !canEditTimingDuration }
                                            options={ timingDurationValueOptions }
                                        />
                                    </Cell>

                                    {/* Duration Unit */ }
                                    <Cell area={ 'durationUnit' } style={ { justifyContent: 'flex-start' } }>
                                        <SelectField
                                            label={ 'Duration Unit' }
                                            name={ 'defaultTimeSpanUnitOverride' }
                                            fluid={ true }
                                            search={ true }
                                            clearable={ true }
                                            disabled={ isLoading || !canEditTimingMilestone }
                                            options={ timingDurationOptions }
                                        />
                                    </Cell>

                                    {/* Corval Earliest Start Statement */ }
                                    <Cell middle area={ 'earliestDate' }>

                                        { ( boundaryStartDate && boundaryStartDate.isValid() ) && (
                                            <Grid className={ 'cursor--pointer' } columns={ `auto 1fr` }>
                                                <Cell>Earliest date: <strong>{ TimeService.asDisplayDate( boundaryStartDate ) }</strong></Cell>
                                                <Cell><Tooltip content={ `A prerequisite activity needs this activity to be scheduled no earlier than ${ TimeService.asDisplayDate( boundaryStartDate ) }.` }/></Cell>
                                            </Grid>
                                        ) }

                                    </Cell>
                                </>
                            ) }

                            {/* Corval Recommendation Statement */ }
                            <Cell middle area={ 'rec' } style={ {
                                display:        'grid',
                                justifyContent: 'flex-end'
                            } }>

                                <CorvalRecommendedStatement value={ `~${ projectActivityDurationString }` }/>

                            </Cell>

                            {/* Summary of Changes */ }
                            <Cell area={ 'summary' }>

                                { isMilestoneTimingType && (
                                    <>
                                        {/* No permissions for editing milestone / duration */ }
                                        { customMilestoneTimingUnavailableForEdits &&
                                          <InfoMessage size={ 'mini' } icon={ null }>This feature is disabled. <HelpAndSupportStatement/></InfoMessage>
                                        }

                                        {/* Summation of timing changes */ }
                                        { ( !includesErrorsIn( form, 'projectTimingMilestoneOverride', 'projectTimingMilestoneOffsetMonthsOverride', ) ) &&
                                          ( hasCustomMilestoneTiming && hasCustomMilestoneOffset && hasCustomMilestoneDuration ) && (

                                              <Segment secondary>
                                                  <FinePrintMessage>

                                                      { `Based on the selection above, this activity will go from ` }

                                                      <><strong>{ TimeService.asDisplayDate( dateRangeInfo.startDate ) }</strong> to <strong>{ TimeService.asDisplayDate( dateRangeInfo.endDate ) }</strong></>

                                                      <StringOrEmpty
                                                          evaluate={ dateRangeInfo.string }
                                                          passValue={ <>&nbsp;( <strong className={ 'text--lowercase' }>{ dateRangeInfo.string }</strong> )</> }/>

                                                      <StringOrEmpty
                                                          evaluate={ `${ dateRangePercentageDiff }` }
                                                          passValue={ (
                                                              <>
                                                                  { ( dateRangePercentageDiff <= dateRangeDiffVariance && dateRangePercentageDiff >= ( -1 * dateRangeDiffVariance ) )
                                                                    ? <>{ `, closely matching the recommendation.` }</>
                                                                    : ( <>
                                                                          {/* Percent increase / decrease statement */ }
                                                                          { ', which is a ' }
                                                                          <strong className={ 'text--lowercase' }>{ dateRangePercentageDiff }% { dateRangePercentageDiffDescriptor }</strong>
                                                                          { ' from the recommended duration.' }

                                                                          {/* Annotation re: timing / budget */ }
                                                                          { ( dateRangePercentageDiff < dateRangeDiffVariance ) && ' Please make sure your team is prepared to perform this activity under a smaller timeframe.' }
                                                                          { ( dateRangePercentageDiff > dateRangeDiffVariance ) && ' Please note that your project budget may increase do to a larger activity duration.' }
                                                                      </> ) }
                                                              </>
                                                          ) }
                                                          emptyValue={ '.' }/>

                                                  </FinePrintMessage>
                                              </Segment>
                                          )
                                        }
                                    </>
                                ) }

                                { !isMilestoneTimingType && (
                                    <>
                                        {/* No permissions for editing end date w/o start date */ }
                                        { customStartTimeUnavailableForEndTimeEdits &&
                                          <InfoMessage size={ 'mini' } icon={ null }>Since there is no custom start time and you only have permission to customize the end date of this activity, this feature is disabled to avoid timing conflicts. <HelpAndSupportStatement/></InfoMessage>
                                        }

                                        {/* No permissions for editing start date w/o end date */ }
                                        { customEndTimeUnavailableForStartTimeEdits &&
                                          <InfoMessage size={ 'mini' } icon={ null }>Since there is no custom start time and you only have permission to customize the end date of this activity, this feature is disabled to avoid timing conflicts. <HelpAndSupportStatement/></InfoMessage>
                                        }

                                        {/* Summation of timing changes */ }
                                        { ( !includesErrorsIn( form, 'startTimeOverrideUtc', 'endTimeOverrideUtc' ) ) && ( hasCustomStartDate && hasCustomEndDate ) &&
                                          <Segment secondary>
                                              <FinePrintMessage>

                                                  { 'Based on the dates above, this activity will take ' }

                                                  <StringOrEmpty
                                                      evaluate={ dateRangeInfo.string }
                                                      passValue={ ( <strong className={ 'text--lowercase' }>{ dateRangeInfo.string }</strong> ) }/>

                                                  <StringOrEmpty
                                                      evaluate={ `${ dateRangePercentageDiff }` }
                                                      passValue={ (
                                                          <>
                                                              { ( dateRangePercentageDiff === 0 )
                                                                ? <>{ ', closely matching to the recommendation.' }</>
                                                                : ( <>
                                                                      {/* Percent increase / decrease statement */ }
                                                                      { ' which is a ' }
                                                                      <strong className={ 'text--lowercase' }>{ dateRangePercentageDiff }% { dateRangePercentageDiffDescriptor }</strong>
                                                                      { ' from the recommended duration.' }

                                                                      {/* Annotation re: timing / budget */ }
                                                                      { ( dateRangePercentageDiff < 0 ) && ' Please make sure your team is prepared to perform this activity under a smaller timeframe.' }
                                                                      { ( dateRangePercentageDiff > 0 ) && ' Please note that your project budget may increase do to a larger activity duration.' }
                                                                  </> ) }
                                                          </>
                                                      ) }
                                                      emptyValue={ '.' }/>

                                              </FinePrintMessage>
                                          </Segment>
                                        }
                                    </>
                                ) }

                            </Cell>

                        </Grid>

                    </Cell>
                ) }


                {/* Recurrence Schedule */ }
                { showRecurrenceScheduling && (
                    <Cell>
                        <FieldWrapper>
                            <FieldLabel>Recurrence Schedule</FieldLabel>
                            <FieldDescription>
                                <FinePrintMessage as={ 'span' }>You can set a schedule for <strong>this</strong> activity to reappear on the map at a given cadence or at certain milestones. When you make an activity recur, Corval will add items to your map that represent this activity later in the
                                    project cycle. These recurring activities won’t affect progression toward your objectives.</FinePrintMessage>
                            </FieldDescription>
                        </FieldWrapper>

                        <Grid columns={ `50% 50%` } columnGap={ '18px' } rows={ `25px auto 1fr` }>

                            <Cell height={ 2 }>

                                {/* Cadence Options */ }
                                { !isMilestoneRecurrenceType && (
                                    <SelectField
                                        fluid
                                        clearable
                                        label={ 'Frequency' }
                                        defaultUpward={ true }
                                        name={ 'recurrenceValueUnitOverride' }
                                        loading={ isLoading }
                                        disabled={ isLoading || !canEditRecurrence }
                                        placeholder={ 'No Recurrence' }
                                        options={ recurrenceFrequencyOptions }
                                        defaultValue={ combineValuesByDelimiter( [ form.values.recurrenceValueOverride, form.values.recurrenceUnitOverride ] ) }
                                        onChange={ handleRecurrenceFrequencyChanged }
                                    />
                                ) }

                                {/* Milestone Options */ }
                                { isMilestoneRecurrenceType && (

                                    <ProjectDetails id={ projectId } get={
                                        `id 
                                        ${ ProjectQueries.fragments.PROJECTS.TIMELINE_BOUNDARY_FIELDS() }
                                        ${ ProjectQueries.fragments.PROJECTS.TIMELINE_FIELDS_AND_DEFINED_TIMINGS() }
                                    ` }>

                                        { ( project: CorvalProject ) => {

                                            let { id, mapLowerBound, mapUpperBound, ...allMilestoneFields } = project,
                                                projectActivityStartTime                                    = TimeService.ISOtoMoment( projectActivity.startTimeUtc, true );

                                            // Collect milestone options
                                            let customTimingMilestones        = ProjectService.getTimelineDataObjectFromProjectFields( project, termDictionary ).filter( milestone => ( milestone.type === ProjectMilestoneTypes.CUSTOM_USER_MILESTONE ) ),
                                                customTimingMilestonesOptions = _.isEmpty( customTimingMilestones ) ? [] : customTimingMilestones.map( m => formatOption( m.fieldId, m.label ) );

                                            // Omit last two milestones for consideration, since these two milestones conflict with scheduling rules;
                                            // then, remove all milestones available that occur after this project activity starts
                                            let omittedMilestones = [
                                                PROJECT_MILESTONES.INVESTIGATIONALNEWDRUGAPPLICATIONFILING
                                            ];

                                            // Omit all milestones that come before the project activity start time.
                                            // _.mapObject( milestoneFields, ( milestoneValue, milestoneField ) => {
                                            // 	if( TimeService.ISOtoMoment( milestoneValue, true ).isBefore( projectActivityStartTime ) ){
                                            // 		omittedMilestones.push( ProjectService.getMilestoneTypeFromField( milestoneField as ProjectMilestoneField ) );
                                            // 	}
                                            // } );

                                            return (
                                                <ProjectTimeMilestonesDropdown
                                                    fluid
                                                    clearable
                                                    multiple
                                                    label={ 'Milestone' }
                                                    defaultUpward={ true }
                                                    name={ 'recurrenceMilestonesOverride' }
                                                    customMilestoneOptions={ customTimingMilestonesOptions }
                                                    loading={ isLoading }
                                                    disabled={ isLoading || !canEditRecurrence }
                                                    placeholder={ 'No Recurrence' }
                                                    omit={ omittedMilestones }
                                                    defaultValue={ null }
                                                />
                                            )
                                        } }
                                    </ProjectDetails>
                                ) }

                            </Cell>

                            <Cell top={ 2 } left={ 2 }>

                                <Grid columns={ `auto 1fr` }
                                      style={ {
                                          transform:       'scale(0.8)',
                                          transformOrigin: 'top center',
                                      } }>

                                    <Cell middle>

                                        <ToggleViewControl
                                            key={ `recurrenceTypeOverride-${ isMilestoneRecurrenceType }` }
                                            toggle={ true }
                                            defaultChecked={ isMilestoneRecurrenceType }
                                            onChange={ handleRecurrenceTypeChanged }
                                        />

                                    </Cell>
                                    <Cell middle onClick={ e => handleRecurrenceTypeChanged( e, { checked: !isMilestoneRecurrenceType } ) } className={ 'cursor--pointer' }>

                                        <span>Use Milestones</span>

                                    </Cell>
                                </Grid>

                            </Cell>

                            { !isMilestoneRecurrenceType && sampleDates && (

                                <Cell width={ 2 }>
                                    <Segment secondary style={ { padding: '5px 10px' } }>

                                        <FinePrintMessage className={ 'margin-xs-bottom' }>Based on the above schedule, here are some approximate recurrent dates for this activity.</FinePrintMessage>

                                        <List horizontal divided size={ 'small' } className={ 'no-margin' }>
                                            { _.first( sampleDates, 5 ).map( ( date, index ) => (
                                                <List.Item key={ index }>
                                                    <Label basic circular>{ TimeService.asDisplayDate( date ) }</Label>
                                                </List.Item>
                                            ) ) }
                                        </List>

                                    </Segment>
                                </Cell>
                            ) }

                        </Grid>
                    </Cell>
                ) }

            </Grid>
        </>
    );
};

ProjectActivityUpdateAreaTiming.defaultProps = defaultProps;

export { ProjectActivityUpdateAreaTiming }
